import React, { useState, useEffect, useRef } from 'react';
import {
    Form,
    Input,
    Button,
    Checkbox,
    ConfigProvider,
    Result,
    InputNumber,
    Radio,
    Alert,
    DatePicker,
    notification,
} from 'antd';
import 'dayjs/locale/es';
import dayjs from 'dayjs';
import { CheckCircleFilled, CloseCircleOutlined } from '@ant-design/icons';
import locale from 'antd/es/date-picker/locale/es_ES';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import changeHeight from '../data/changeHeight';

const FINANC_SI = 'Sí';
const FINANC_NO = 'No';
const FINANC_INCIERTO = 'No estoy seguro';

const POST_ENDPOINT = 'https://n8n-615087458109.europe-southwest1.run.app/webhook/3d6fcfff-fd5a-4f4f-87f2-468cb709635f';

const FormPrestamosConsumo = () => {
    const [buttonLoading, setbuttonLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [primaryColor, setPrimaryColor] = useState('#FFFFFF');
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const refForm = useRef(null);
    const refResult = useRef(null);
    const [result, setResult] = useState('collapse');
    const [heightResult, setHeightResult] = useState(0);
    const [api, contextHolder] = notification.useNotification();
    const [step, setStep] = useState(1);

    const financiacionExtra = Form.useWatch('financiacion_extra', form);

    useEffect(() => {
        if (searchParams.get('primaryColor')) {
            setPrimaryColor('#' + searchParams.get('primaryColor'));
            setResult('none');
            if (refResult.current) {
                setHeightResult(refResult.current.clientHeight);
            }
        }
        if (searchParams.get('parentUrl')) {
            const rawParentUrl = searchParams.get('parentUrl');
            let parentUrl = rawParentUrl;
            try {
                parentUrl = decodeURIComponent(rawParentUrl);
            } catch {
                parentUrl = rawParentUrl;
            }
            form.setFieldsValue({ url: parentUrl });
        }
    }, [searchParams, form]);

    useEffect(() => {
        const el = refForm.current;
        if (!el) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === el) {
                    changeHeight(entry.target.clientHeight);
                    break;
                }
            }
        });

        resizeObserver.observe(el);

        return () => {
            resizeObserver.unobserve(el);
        };
    }, [step]);

    useEffect(() => {
        const onMessage = (event) => {
            if (event.data.type === 'from-parent') {
                if (
                    event.data.fbp === 'null' ||
                    event.data.fbp === null ||
                    event.data.fbp === undefined
                ) {
                    form.setFieldsValue({ fbp: ' ' });
                } else {
                    form.setFieldsValue({ fbp: event.data.fbp });
                }
                if (
                    event.data.hubspotutk === 'null' ||
                    event.data.hubspotutk === null ||
                    event.data.hubspotutk === undefined
                ) {
                    form.setFieldsValue({ hubspotutk: ' ' });
                } else {
                    form.setFieldsValue({
                        hubspotutk: event.data.hubspotutk,
                    });
                }
                if (event.data.userAgent) {
                    form.setFieldsValue({ userAgent: event.data.userAgent });
                }
                if (refForm.current) {
                    changeHeight(refForm.current.clientHeight);
                }
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [form]);

    const disabledFutureYears = (current) => {
        return current && current > dayjs().endOf('year');
    };

    const openNotification = (placement) => {
        api.info({
            message: 'Error inesperado',
            description:
                'Ha ocurrido un error inesperado. Inténtelo más tarde. Disculpe las molestias',
            placement,
            icon: <CloseCircleOutlined style={{ color: '#f7151c' }} />,
        });
    };

    const onStepOneFinish = () => {
        if (financiacionExtra === FINANC_NO) {
            return;
        }
        setStep(2);
        setTimeout(() => {
            if (refForm.current) changeHeight(refForm.current.clientHeight);
        }, 0);
    };

    const buildPayload = async () => {
        const vals = await form.validateFields();
        const anio = vals.anio_firma
            ? dayjs(vals.anio_firma).format('YYYY')
            : '';

        return {
            url: vals.url,
            web_origen: vals.url,
            fbp: vals.fbp,
            hubspotutk: vals.hubspotutk,
            userAgent: vals.userAgent,
            anio_firma: anio,
            duracion_prestamo: vals.duracion_prestamo,
            financiacion_extra: vals.financiacion_extra,
            nombre: vals.nombre,
            email: vals.email,
            telefono: vals.telefono,
            terms: vals.terms,
            formulario: 'prestamos_consumo_vinculado',
        };
    };

    const onConfirmSubmit = async () => {
        if (!POST_ENDPOINT) {
            api.error({
                message: 'Configuración pendiente',
                description:
                    'No se puede realizar la solicitud de reclamación de préstamos vinculados al consumo.',
                placement: 'bottom',
                icon: <CloseCircleOutlined style={{ color: '#f7151c' }} />,
            });
            return;
        }

        setbuttonLoading(true);
        try {
            const payload = await buildPayload();
            await axios.post(POST_ENDPOINT, payload);
            setbuttonLoading(false);
            setSuccess(true);
            setResult('block');
            changeHeight(heightResult || refResult.current?.clientHeight);
        } catch {
            openNotification('bottom');
            setbuttonLoading(false);
        }
    };

    const labelStyle = {
        fontSize: '15px',
        marginTop: '-6px',
        marginBottom: '-6px',
    };

    const renderFinanciacionAlerts = () => {
        if (financiacionExtra === FINANC_NO) {
            return (
                <div style={{ marginTop: '3%', marginBottom: '3%' }}>
                    <Alert
                        message={
                            <>
                                Importante: si tu préstamo{' '}
                                <strong>no incluye costes financiados</strong> (como seguro o
                                comisión de apertura, entre otros), <strong>no se puede reclamar</strong>.
                                Si no estás seguro, puedes consultarnos tu caso y te lo revisamos de forma gratuita seleccionando la opción "No estoy seguro".
                            </>
                        }
                        type="error"
                        showIcon
                    />
                </div>
            );
        }
        if (financiacionExtra === FINANC_INCIERTO) {
            return (
                <div style={{ marginTop: '3%', marginBottom: '3%' }}>
                    <Alert
                        message="Si no estás seguro, puedes consultarnos tu caso y te lo revisamos de forma gratuita."
                        type="info"
                        showIcon
                    />
                </div>
            );
        }
        return null;
    };

    const getSummaryValues = () => {
        const v = form.getFieldsValue([
            'anio_firma',
            'duracion_prestamo',
            'financiacion_extra',
            'nombre',
            'email',
            'telefono',
        ]);
        const anioStr = v.anio_firma ? dayjs(v.anio_firma).format('YYYY') : '—';

        return {
            ...v,
            anio_firma_label: anioStr,
        };
    };

    const subtitleText =
        'Tu información ha sido enviada correctamente.<br/>En breve nos pondremos en contacto contigo.';

    return (
        <>
            {contextHolder}
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: primaryColor,
                    },
                }}
            >
                <style>
                    {`
                    ::-webkit-scrollbar {
                        display: none;
                    }
                    `}
                </style>

                <div ref={refForm}>
                    <Form
                        form={form}
                        style={{ padding: '1rem' }}
                        hidden={success}
                        name="prestamosConsumo"
                        initialValues={{
                            remember: true,
                            financiacion_extra: undefined,
                        }}
                        layout="vertical"
                        onFinish={
                            step === 1 ? onStepOneFinish : undefined
                        }
                        requiredMark
                    >
                                <Form.Item
                                    hidden
                                    label="URL"
                                    name="url"
                                    rules={[{ required: false }]}
                                >
                                    <Input type="text" />
                                </Form.Item>

                                <Form.Item hidden label="FB" name="fbp">
                                    <Input />
                                </Form.Item>

                                <Form.Item
                                    hidden
                                    label="Hubspotutk"
                                    name="hubspotutk"
                                >
                                    <Input />
                                </Form.Item>

                                <Form.Item
                                    hidden
                                    label="userAgent"
                                    name="userAgent"
                                >
                                    <Input />
                                </Form.Item>

                                <div
                                    style={{
                                        display: step === 1 ? 'block' : 'none',
                                    }}
                                >

                                    <Form.Item
                                        label={
                                            <p style={labelStyle}>
                                                ¿En qué año firmaste tu préstamo?
                                            </p>
                                        }
                                        name="anio_firma"
                                        style={{ marginBottom: '0.6rem' }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Por favor, indica el año de firma',
                                            },
                                        ]}
                                    >
                                        <DatePicker
                                            picker="year"
                                            style={{ width: '100%' }}
                                            format="YYYY"
                                            locale={locale}
                                            disabledDate={disabledFutureYears}
                                            placeholder="Selecciona el año"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label={
                                            <p style={labelStyle}>
                                                Duración del préstamo (en años)
                                            </p>
                                        }
                                        name="duracion_prestamo"
                                        style={{ marginBottom: '0.6rem' }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Por favor, indica la duración del préstamo en años',
                                            },
                                            {
                                                type: 'number',
                                                min: 1,
                                                max: 50,
                                                message:
                                                    'Introduce un plazo entre 1 y 50 años',
                                            },
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            addonAfter="años"
                                            min={1}
                                            max={50}
                                            placeholder="Ej.: 15"
                                            disabled={buttonLoading}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label={
                                            <p style={labelStyle}>
                                                ¿Te financiaron dentro del préstamo un
                                                seguro o una comisión de apertura...?
                                            </p>
                                        }
                                        name="financiacion_extra"
                                        style={{ marginBottom: '0.6rem' }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Por favor, selecciona una opción',
                                            },
                                        ]}
                                    >
                                        <Radio.Group
                                            disabled={buttonLoading}
                                        >
                                            <Radio value={FINANC_SI}>
                                                {FINANC_SI}
                                            </Radio>
                                            <Radio value={FINANC_NO}>
                                                {FINANC_NO}
                                            </Radio>
                                            <Radio value={FINANC_INCIERTO}>
                                                {FINANC_INCIERTO}
                                            </Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    {renderFinanciacionAlerts()}

                                    <Form.Item
                                        label={
                                            <p style={labelStyle}>
                                                Nombre y apellidos
                                            </p>
                                        }
                                        name="nombre"
                                        style={{ marginBottom: '0.6rem' }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Por favor, ingresa tu nombre completo',
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Nombre y apellidos"
                                            disabled={buttonLoading}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label={
                                            <p style={labelStyle}>Email</p>
                                        }
                                        name="email"
                                        style={{ marginBottom: '0.6rem' }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Por favor, introduce tu email',
                                            },
                                            { type: 'email', message: 'Email no válido' },
                                        ]}
                                    >
                                        <Input
                                            type="email"
                                            placeholder="tuemail@ejemplo.com"
                                            disabled={buttonLoading}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label={
                                            <p style={labelStyle}>Teléfono</p>
                                        }
                                        name="telefono"
                                        style={{ marginBottom: '0.6rem' }}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Por favor, introduce tu teléfono',
                                            },
                                            {
                                                len: 9,
                                                message:
                                                    'El teléfono debe tener 9 dígitos',
                                            },
                                        ]}
                                    >
                                        <Input
                                            type="tel"
                                            placeholder="Ej.: 919993131"
                                            maxLength={9}
                                            disabled={buttonLoading}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        style={{ marginBottom: '0rem' }}
                                        valuePropName="checked"
                                        name="terms"
                                        initialValue={false}
                                        rules={[
                                            {
                                                required: true,
                                                validator: async (_, checked) => {
                                                    if (!checked) {
                                                        return Promise.reject(
                                                            new Error(
                                                                'Por favor, acepta los términos y condiciones'
                                                            )
                                                        );
                                                    }
                                                },
                                            },
                                        ]}
                                    >
                                        <Checkbox
                                            style={{ fontSize: '15px' }}
                                            disabled={buttonLoading}
                                        >
                                            Acepto los{' '}
                                            <Link
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                to="https://www.coventia.es/politica-de-privacidad-y-aviso-de-cookies"
                                            >
                                                términos y condiciones
                                            </Link>
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                                        {() => (
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                disabled={
                                                    financiacionExtra ===
                                                    FINANC_NO
                                                }
                                                loading={buttonLoading}
                                                style={{
                                                    width: '100%',
                                                    height: '35px',
                                                }}
                                            >
                                                Continuar
                                            </Button>
                                        )}
                                    </Form.Item>
                                </div>

                                <div
                                    style={{
                                        display: step === 2 ? 'block' : 'none',
                                    }}
                                >
                                    {(() => {
                                        const s = getSummaryValues();
                                        return (
                                            <div
                                                style={{
                                                    marginBottom: '1rem',
                                                    width: '100%',
                                                }}
                                            >
                                                <p
                                                    style={{
                                                        margin: '0 0 0.65rem',
                                                        fontSize: '15px',
                                                    }}
                                                >
                                                    Revisa tus datos antes de enviar
                                                </p>
                                                <p style={{ margin: '0.35rem 0' }}>
                                                    <strong>
                                                        Año de firma del préstamo:
                                                    </strong>{' '}
                                                    {s.anio_firma_label}
                                                </p>
                                                <p style={{ margin: '0.35rem 0' }}>
                                                    <strong>
                                                        Duración del préstamo (en años):
                                                    </strong>{' '}
                                                    {s.duracion_prestamo ?? '—'}
                                                </p>
                                                <p style={{ margin: '0.35rem 0' }}>
                                                    <strong>
                                                        ¿Se financió un seguro o comisión
                                                        dentro del préstamo?:
                                                    </strong>{' '}
                                                    {s.financiacion_extra ?? '—'}
                                                </p>
                                                <p style={{ margin: '0.35rem 0' }}>
                                                    <strong>Nombre y apellidos:</strong>{' '}
                                                    {s.nombre ?? '—'}
                                                </p>
                                                <p style={{ margin: '0.35rem 0' }}>
                                                    <strong>Email:</strong> {s.email ?? '—'}
                                                </p>
                                                <p style={{ margin: '0.35rem 0' }}>
                                                    <strong>Teléfono:</strong>{' '}
                                                    {s.telefono ?? '—'}
                                                </p>
                                            </div>
                                        );
                                    })()}
                                    <Button
                                        type="default"
                                        disabled={buttonLoading}
                                        style={{
                                            width: '100%',
                                            marginBottom: '0.6rem',
                                        }}
                                        onClick={() => {
                                            setStep(1);
                                            setTimeout(() => {
                                                if (refForm.current)
                                                    changeHeight(
                                                        refForm.current.clientHeight
                                                    );
                                            }, 0);
                                        }}
                                    >
                                        Editar datos
                                    </Button>
                                    <Button
                                        type="primary"
                                        loading={buttonLoading}
                                        style={{ width: '100%', height: '35px' }}
                                        onClick={onConfirmSubmit}
                                    >
                                        Confirmar y enviar
                                    </Button>
                                </div>
                            </Form>
                </div>

                <div ref={refResult}>
                    <div style={{ display: `${result}` }}>
                        <Result
                            icon={
                                <CheckCircleFilled
                                    style={{ color: primaryColor }}
                                />
                            }
                            style={{
                                padding: '1.5rem',
                                paddingTop: '3rem',
                            }}
                            status="success"
                            title="¡Gracias!"
                            subTitle={
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: subtitleText,
                                    }}
                                />
                            }
                            extra={[
                                <Button
                                    type="primary"
                                    key="cita"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href="https://meetings-eu1.hubspot.com/santiago-gatti"
                                >
                                    Haz clic para concertar una cita
                                </Button>,
                            ]}
                        />
                            </div>
                </div>
            </ConfigProvider>
        </>
    );
};

export default FormPrestamosConsumo;
